import { Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable
} from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AuthStore,
  ConfirmDialogResult,
  DistributionStrategyType,
  formatAccess,
  formatCompatibleVersions,
  openConfirmDialog,
  openRejectConfirmDialog,
  PageWrapper,
  RolloutPlanChangeHistoryDto,
  RolloutPlanDetailsDto,
  RolloutPlanStatisticsDto,
  RolloutPlanStore,
  UnsavedChangesGuardedComponent,
  useRolloutPlanStore
} from '@ottobock/remote-update/domain';
import { DistributionSection, ENV_CONFIG, LogService } from '@ottobock/shared/domain';

import { CreateRolloutPlan } from '../rollout-plan-modal/create-rollout-plan';

import { CounterPerTargetProductGroup } from './counter-per-target-product-group/counter-per-target-product-group.component';
import { RolloutPlanChangeHistory } from './rollout-plan-change-history/rollout-plan-history.component';
import { RolloutPlanDetailsService } from './rollout-plan-details.service';

@Component({
  selector: 'remote-update-feature-remote-update-dashboard-rollout-details',
  imports: [
    PageWrapper,
    MatButton,
    MatProgressBar,
    RolloutPlanChangeHistory,
    CounterPerTargetProductGroup,
    ReactiveFormsModule,
    MatButtonToggleModule,
    MatCheckboxModule,
    DistributionSection,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatCellDef,
    MatHeaderCell,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRow,
    MatRowDef
  ],
  templateUrl: './rollout-plan-details.html',
  styleUrl: './rollout-plan-details.css'
})
export class RolloutPlanDetails implements UnsavedChangesGuardedComponent, OnInit {
  private snackBar = inject(MatSnackBar);
  private rolloutPlanFormStore = inject(RolloutPlanStore);
  private router = inject(Router);
  private logService = inject(LogService);
  private envConfig = inject(ENV_CONFIG);
  readonly isSandbox = this.envConfig.isSandbox;
  authStore = inject(AuthStore);
  route = inject(ActivatedRoute);
  dialog = inject(MatDialog);
  rolloutPlanDetailsService = inject(RolloutPlanDetailsService);
  rolloutPlanStore = inject(useRolloutPlanStore);
  rolloutPlanStatistics = signal<RolloutPlanStatisticsDto>({
    lastUpdated: '',
    productGroups: []
  });

  isReviewRequestedByCurrentUser = signal<boolean | null>(null);

  backRedirectRoute = '/rollout-plan';

  get hasUnsavedChanges() {
    return this.rolloutPlanFormStore.isFormDirty();
  }
  displayedColumns = ['targetProductGroups', 'btFriendlyNames'];

  rolloutPlanHistoryHasError = computed(
    () => this.rolloutPlanStore.rolloutPlanChangeHistory().error
  );
  isLoadingRolloutPlanChangeHistory = computed(
    () => this.rolloutPlanStore.rolloutPlanChangeHistory().loading
  );
  rolloutPlanChangeHistoryData = computed<RolloutPlanChangeHistoryDto[]>(
    () => this.rolloutPlanStore.rolloutPlanChangeHistory().data ?? []
  );

  rolloutPlanStatisticHasError = computed(
    () => this.rolloutPlanStore.countersPerTargetProductGroup().error
  );
  isLoadingRolloutPlanStatistics = computed(
    () => this.rolloutPlanStore.countersPerTargetProductGroup().loading
  );

  hasError = computed(() => this.rolloutPlanStore.rolloutPlanDetails().error);
  isLoadingRolloutPlanDetails = computed(() => this.rolloutPlanStore.rolloutPlanDetails().loading);
  rolloutPlanId = 0;
  rolloutPlanDetails = signal<RolloutPlanDetailsDto>({
    rolloutPlanId: 0,
    packageId: 0,
    packageVersion: '',
    status: '',
    strategy: {
      strategyType: DistributionStrategyType.Percentage,
      productGroupPercentages: []
    },
    productGroupPercentages: [
      {
        productGroupId: 0,
        productGroupName: '',
        percentage: 0
      }
    ],
    packageDistribution: {
      access: 'cpo',
      supportedCompanionApps: [
        {
          name: '',
          appleMinimumVersion: '0.0.0',
          androidMinimumVersion: '0.0.0'
        }
      ]
    },
    compatibleVersions: [
      {
        cpNumber: 0,
        hardwareId: 0,
        major: 0,
        minor: 0,
        develop: 0
      }
    ],
    updatedAt: 0,
    possibleActions: ['CanActivate']
  });
  readonly rolloutStatus = computed(() => this.rolloutPlanDetails().status);
  readonly isDraft = computed(() => this.rolloutStatus() === 'Draft');

  readonly isReadyForReview = computed(() => this.rolloutStatus() === 'Ready for review');

  readonly isActive = computed(() => this.rolloutStatus() === 'Active');

  readonly isInactive = computed(() => this.rolloutStatus() === 'Inactive');

  readonly isActiveOrInactive = computed(() => this.isActive() || this.isInactive());
  readonly nextStatus = computed(() => (this.isActive() ? 'Inactive' : 'Active'));

  readonly canStopRollout = computed(
    () => this.isActiveOrInactive() && this.rolloutPlanDetails().possibleActions.includes('CanStop')
  );

  formattedAccess = computed(() => {
    return this.rolloutPlanDetails()
      ? formatAccess(this.rolloutPlanDetails().packageDistribution.access)
      : '';
  });

  formatCompatibleVersions = computed(() => {
    return this.rolloutPlanDetails().compatibleVersions
      ? formatCompatibleVersions(this.rolloutPlanDetails().compatibleVersions)
      : [];
  });

  getWhitelistGroups(): {
    productGroupId: number;
    productGroupName: string;
    btFriendlyNames: string[];
  }[] {
    const strategy = this.rolloutPlanDetails().strategy;
    if (strategy.strategyType === DistributionStrategyType.Whitelist) {
      return strategy.productGroupWhitelists;
    }
    return [];
  }

  getPercentageGroups(): {
    productGroupId: number;
    productGroupName: string;
    percentage: number;
  }[] {
    const strategy = this.rolloutPlanDetails().strategy;
    if (strategy.strategyType === DistributionStrategyType.Percentage) {
      return strategy.productGroupPercentages;
    }
    return [];
  }

  async ngOnInit() {
    this.rolloutPlanId = Number(this.route.snapshot.paramMap.get('id'));
    await this.rolloutPlanDetailsService.getRolloutPlanDetails(this.rolloutPlanId);
    await this.rolloutPlanStore.loadRolloutPlanChangeHistory(this.rolloutPlanId);

    const statistics = await this.rolloutPlanDetailsService.getRolloutPlanStatistics(
      this.rolloutPlanId
    );
    this.rolloutPlanStatistics.set(statistics);
  }

  rolloutPlanDetailsEffect = effect(() => {
    const details = this.rolloutPlanStore.rolloutPlanDetails().data;
    if (details) {
      this.rolloutPlanDetails.set(details);
    }

    const history = this.rolloutPlanChangeHistoryData();
    const currentUser = this.authStore.accountInfo()?.idTokenClaims?.sub;

    if (!history.length || !currentUser) {
      this.isReviewRequestedByCurrentUser.set(null);
      return;
    }
    // Check if the current user is the one who requested review
    this.isReviewRequestedByCurrentUser.set(history[0].user === currentUser);
  });

  openApproveConfirmationDialog() {
    const confirmationDialog = openConfirmDialog(this.dialog, {
      title: 'Confirm activation',
      message:
        'Approving will set the rollout plan to ‘Active’ and start the update rollout to devices.\nAre you sure you want to proceed?',
      rightButton: 'Approve activation',
      height: '225px'
    });
    confirmationDialog.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed) return;
      try {
        await this.rolloutPlanDetailsService.approveRolloutPlan(this.rolloutPlanId);
        this.snackBar.open(`Rollout plan approved successfully!`, 'Close');
      } catch (error: unknown) {
        this.logService.error(`Failed to approve rollout plan`, error);
        this.snackBar.open('Something went wrong. Try again.', 'Close');
      }
    });
  }

  openRejectConfirmationDialog() {
    const confirmationDialog = openRejectConfirmDialog(this.dialog, {
      title: 'Confirm rejection',
      message: `Rejecting will set the rollout plan back to “Draft” and prevent the update rollout to devices.\nAre you sure you want to reject activation?`,
      rightButton: 'Reject activation',
      height: '380px'
    });
    confirmationDialog.afterClosed().subscribe(async (result: ConfirmDialogResult | undefined) => {
      if (!result?.confirmed) return;
      // ✅ Reason from textarea
      const reason = result.reason;
      try {
        await this.rolloutPlanDetailsService.rejectRolloutPlan(this.rolloutPlanId, reason);
        this.snackBar.open(`Rollout plan rejected successfully!`, 'Close');
      } catch (error: unknown) {
        this.logService.error(`Failed to reject rollout plan`, error);
        this.snackBar.open('Something went wrong. Try again.', 'Close');
      }
    });
  }

  openStopConfirmationDialog() {
    const confirmationDialog = openRejectConfirmDialog(this.dialog, {
      title: 'Confirm stop',
      message: `Stopping a rollout plan is a permanent action and cannot be undone.\nAre you sure you want to stop the rollout plan?`,
      rightButton: 'Stop rollout plan',
      placeholder: 'Stopping remarks',
      helperText: 'Add the reason why you stop the rollout plan (max.500 characters).',
      height: '380px'
    });
    confirmationDialog.afterClosed().subscribe(async (result: ConfirmDialogResult | undefined) => {
      if (!result?.confirmed) return;
      // ✅ Reason from textarea
      const reason = result.reason;
      try {
        await this.rolloutPlanDetailsService.stopRolloutPlan(this.rolloutPlanId, reason);
        this.snackBar.open(`Rollout plan stopped successfully!`, 'Close');
      } catch (error: unknown) {
        this.logService.error(`Failed to stop rollout plan`, error);
        this.snackBar.open('Something went wrong. Try again.', 'Close');
      }
    });
  }

  openChangeStatusConfirmationDialog(newStatus: RolloutPlanDetailsDto['status']) {
    const confirmationDialog = openConfirmDialog(this.dialog, {
      message: `Are you sure you want to set this rollout plan ${newStatus.toLowerCase()}?`,
      rightButton: newStatus === 'Active' ? 'Activate' : 'Deactivate'
    });
    confirmationDialog.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed) return;
      try {
        if (newStatus === 'Active') {
          await this.rolloutPlanDetailsService.activateRolloutPlan(this.rolloutPlanId);
        } else {
          await this.rolloutPlanDetailsService.deactivateRolloutPlan(this.rolloutPlanId);
        }
        this.snackBar.open(
          `Rollout plan ${newStatus === 'Active' ? 'activated' : 'deactivated'}  successfully!`,
          'Close'
        );
      } catch (error: unknown) {
        this.logService.error(
          `Failed to ${newStatus === 'Active' ? 'activate' : 'deactivate'} rollout plan`,
          error
        );
        this.snackBar.open('Something went wrong. Try again.', 'Close');
      }
    });
  }

  private async updateStateIfUnchangedOnEditModelCancel() {
    try {
      await this.rolloutPlanDetailsService.rejectRolloutPlan(this.rolloutPlanId, null);
    } catch (error: unknown) {
      this.logService.error(
        'Error occured while updating the rollout plan on cancel edit model',
        error
      );
      const message = 'Something went wrong. Try again.';
      this.snackBar.open(message, 'Close');
    }
  }

  private openEditDialog() {
    this.rolloutPlanFormStore.setEditingRolloutPlan(true);
    this.openDialog();
  }

  editRolloutPlan() {
    if (this.rolloutPlanDetails().status === 'Ready for review') {
      return this.openConfirmEditDialog();
    }
    this.openEditDialog();
    this.rolloutPlanFormStore.setEditingRolloutPlan(true);
  }

  openDialog() {
    this.rolloutPlanFormStore.resetFormData();
    const dialogRef = this.dialog.open(CreateRolloutPlan, {
      width: '100%',
      maxWidth: '100%',
      height: '100%',
      data: { rolloutPlanDetails: this.rolloutPlanDetails() },
      disableClose: true,
      closeOnNavigation: false,
      panelClass: 'no-radius-dialog'
    });

    dialogRef.afterClosed().subscribe(() => {
      this.rolloutPlanFormStore.resetFormData();
    });
  }
  openDeleteConfirmationDialog() {
    const confirmationDialog = openConfirmDialog(this.dialog, {
      message: 'You are about to delete the rollout plan. This cannot be undone.',
      rightButton: 'Delete',
      isDangerous: true
    });

    confirmationDialog.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed) return;
      try {
        await this.rolloutPlanDetailsService.deleteRolloutPlan(this.rolloutPlanId);
        this.snackBar.open('Rollout plan deleted successfully!', 'Close');
        this.router.navigate(['/rollout-plan']);
      } catch (error: unknown) {
        this.logService.error('Failed to delete the rollout plan', error);
        this.snackBar.open('Something went wrong. Try again.', 'Close');
      }
    });
  }

  openConfirmEditDialog() {
    const confirmationDialog = openConfirmDialog(this.dialog, {
      title: 'Confirm edit',
      message: `Editing a rollout plan pending approval will set its state to “Draft” and requires approval, even if no changes are made.\nAre you sure you want to continue?`,
      rightButton: 'Confirm and edit',
      height: '230px'
    });

    confirmationDialog.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed) return;
      this.updateStateIfUnchangedOnEditModelCancel();
      this.openEditDialog();
    });
  }

  markRolloutReadyForReview() {
    try {
      this.rolloutPlanDetailsService.markRolloutPlanReadyForReview(this.rolloutPlanId);
      this.snackBar.open('Rollout plan set to ready for review', 'Close');
    } catch (error: unknown) {
      this.logService.error('Failed to set the rollout plan to ready for review', error);
      this.snackBar.open('Something went wrong. Try again.', 'Close');
    }
  }

  height = 'min-h-0';
  bgColor = 'bg-[#fff]';
  paddingX = 'px-0';
  emptyTitle = 'No product groups & rollout percentages found';
  emptyDescription = 'There are no details for the target product groups & rollout percentages';

  emptyChangeHistoryTitle = 'No change history found';
  emptyChangeHistoryDescription = 'There are no changes recorded for this rollout plan yet.';

  emptyRolloutStatisticsTitle = 'No statistics found';
  emptyRolloutStatisticsDescription = 'There are no statistics recorded for this rollout plan yet.';
}
